/**
 * The tenant-level role vocabulary, as Auth Postgres stores it and Keycloak
 * projects it (ADR-0021; the Transformation Lead amendment).
 *
 * Roles are ADDITIVE. Every active member holds `tenant-member`; `tenant-admin`
 * and `transformation-lead` are independent grants layered on top, and neither
 * implies the other: a Tenant Admin does not thereby manage LAMA, and a
 * Transformation Lead does not thereby administer the workspace. The wire
 * contract is therefore a role SET (`roles: TenantRole[]`), never a single
 * select value that would make the three mutually exclusive.
 *
 * Lives in `@playbookai/types` so the shell, the LAMA feature and playbook-be
 * read ONE spelling. `services/auth` cannot depend on this package (it has no
 * workspace link to it) and carries the same literals in
 * `services/auth/src/access/access.types.ts`; `roles.spec.ts` on each side pins
 * the values so the two copies cannot drift silently.
 */
export const TENANT_ROLES = [
    "tenant-member",
    "tenant-admin",
    "transformation-lead",
];
export const TENANT_MEMBER_ROLE = "tenant-member";
export const TENANT_ADMIN_ROLE = "tenant-admin";
export const TRANSFORMATION_LEAD_ROLE = "transformation-lead";
/** The one display name for the role, everywhere it is shown. */
export const TRANSFORMATION_LEAD_DISPLAY_NAME = "Transformation Lead";
export function isTenantRole(value) {
    return (typeof value === "string" &&
        TENANT_ROLES.includes(value));
}
/**
 * Whether a claim/role list grants LAMA MANAGEMENT — questionnaires,
 * campaigns and reports. EXACT-role semantics: only the literal
 * `transformation-lead` counts. `tenant-admin` is deliberately not an alias
 * (AC2 / AC7 of the plan), and there is no snake-case tolerance the way the
 * shell's `canAdministerWorkspace` has, because nothing has ever emitted one.
 *
 * Affordance-only on the frontend; `TransformationLeadGuard` in playbook-be is
 * the authoritative gate.
 */
export function canManageLama(roles) {
    return roles?.includes(TRANSFORMATION_LEAD_ROLE) ?? false;
}
/**
 * Canonicalise a desired role set for a write: unknown values are dropped,
 * duplicates collapse, `tenant-member` is always present (an active member
 * cannot hold an additive role without the base one), and the order is the
 * vocabulary order so two callers describing the same set send the same bytes.
 */
export function normalizeTenantRoles(roles) {
    const held = new Set([TENANT_MEMBER_ROLE]);
    for (const role of roles) {
        if (isTenantRole(role))
            held.add(role);
    }
    return TENANT_ROLES.filter((role) => held.has(role));
}
//# sourceMappingURL=tenant-roles.js.map